import { Component, h, Host, Prop, Element } from '@stencil/core';
import {
    IAnimatedButtonIconParams,
    IAnimatedButtonParams,
    IAnimatedButtonFontParams,
    animTextNoLineBlackBlueBG,
    asSans16,
    animTextNoLineWhiteBlueBG,
} from './arv-button-animated-params';
import { ArvIcons } from 'apps/arv-components/src/global/arv-icons';
@Component({
    tag: 'arv-button-animated',
    styleUrl: 'arv-button-animated.scss',
    shadow: true,
})
export class ArvButtonAnimated {
    @Prop() type: 'None' | 'Primary' | 'Secondary' = 'None'; // This is important for "csb-dialog-form"
    @Prop({ reflect: true }) asHref: boolean = true;
    @Prop({ reflect: true }) text: string;
    @Prop({ reflect: true }) url: string;
    @Prop({ reflect: true }) target: string;
    @Prop({ reflect: true }) colorTheme: IAnimatedButtonParams;
    @Prop({ reflect: true }) fontTheme: IAnimatedButtonFontParams;
    @Prop({ reflect: true }) iconTheme: IAnimatedButtonIconParams;
    @Prop({ reflect: true }) clickCallBack?: (event: Event) => void;
    @Prop({ reflect: true }) active: boolean;
    @Prop({ reflect: true }) invertLineVisibility: boolean;
    @Prop({ reflect: true }) initAsDefaultButton: boolean = true;
    @Prop({ reflect: true }) focusBorder?: boolean = true;
    @Prop({ reflect: true }) ariaLabel?: string;
    @Prop({ reflect: true }) ariaExpanded: boolean = false;

    @Element() element;

    componentWillRender() {
        if (this.initAsDefaultButton) {
            this.colorTheme = animTextNoLineBlackBlueBG();
            this.fontTheme = asSans16();
            this.asHref = this.url ? true : false;
        }
        this.setColorTheme(this.colorTheme);
    }
    render() {
        switch (this.type) {
            case 'None':
                break;
            case 'Primary':
                this.fontTheme = asSans16();
                this.colorTheme = animTextNoLineBlackBlueBG();
                this.setColorTheme(this.colorTheme);
                break;
            case 'Secondary':
                this.fontTheme = asSans16();
                this.colorTheme = animTextNoLineWhiteBlueBG();
                this.setColorTheme(this.colorTheme);
                break;
        }

        if (this.asHref) {
            return (
                <Host class={{ active: this.active, invertLineVisibility: this.invertLineVisibility }}>
                    <a class={{ 'icon-text': true, padding: this.colorTheme.needsBackgroundPadding }} href={this.url} title={this.text} target={this.target}>
                        {this.genereateIcon('left')} {this.renderList()}
                        {this.genereateIcon('right')}
                    </a>
                </Host>
            );
        }
        return (
            <Host class={{ active: this.active }}>
                <button
                    class={{ 'icon-text': true, padding: this.colorTheme.needsBackgroundPadding, 'blue-focus': this.focusBorder }}
                    onClick={event => this.clickCallBack?.(event)}
                    aria-expanded={this.ariaExpanded?.toString()}
                >
                    {this.genereateIcon('left')} {this.renderList()}
                    {this.genereateIcon('right')}
                </button>
            </Host>
        );
    }

    private genereateIcon(direction: string) {
        if (this.iconTheme?.direction == direction) {
            let t = this.iconTheme;
            return ArvIcons.getGeneric(t.name, t.color, t.size, t.className + ' ' + t.direction);
        }
    }

    private renderList() {
        return (
            <div class="container">
                <div class="content">{this.text}</div>
                <div class="content" aria-hidden="true">
                    {this.text}
                </div>
            </div>
        );
    }

    static asHref(
        text: string,
        url: string,
        target: string,
        theme: IAnimatedButtonParams,
        fontTheme: IAnimatedButtonFontParams,
        iconTheme?: IAnimatedButtonIconParams,
        active: boolean = false,
        hasInvertedUnderline: boolean = false,
    ) {
        return (
            <arv-button-animated
                asHref={true}
                text={text}
                url={url}
                target={target}
                colorTheme={theme}
                fontTheme={fontTheme}
                iconTheme={iconTheme}
                active={active}
                initAsDefaultButton={false}
                invertLineVisibility={hasInvertedUnderline}
            ></arv-button-animated>
        );
    }

    static asButton(
        text: string,
        theme: IAnimatedButtonParams,
        fontTheme: IAnimatedButtonFontParams,
        iconTheme?: IAnimatedButtonIconParams,
        clickCallBack?: (event: Event) => void,
        active: boolean = false,
        focusBorder: boolean = true,
        ariaLabel?: string,
        ariaExpanded: boolean = false,
    ) {
        return (
            <arv-button-animated
                asHref={false}
                text={text}
                colorTheme={theme}
                fontTheme={fontTheme}
                iconTheme={iconTheme}
                clickCallBack={clickCallBack}
                active={active}
                initAsDefaultButton={false}
                focusBorder={focusBorder}
                ariaLabel={ariaLabel}
                ariaExpanded={ariaExpanded}
            ></arv-button-animated>
        );
    }

    setColorTheme(theme: IAnimatedButtonParams) {
        this.invertLineVisibility = theme.invertLineVisibility;
        this.element.style.setProperty('--default-background-rgba', theme.defaultBackgroundRGBAHex);
        this.element.style.setProperty('--default-text-rgba', theme.defaultTextRGBAHex);
        this.element.style.setProperty('--default-underline-rgba', theme.defaultUnderlineRGBAHex);
        this.element.style.setProperty('--default-border-rgba', theme.defaultBorderRGBAHex);

        this.element.style.setProperty('--hover-background-rgba', theme.hoverBackgroundRGBAHex);
        this.element.style.setProperty('--hover-text-rgba', theme.hoverTextRGBAHex);
        this.element.style.setProperty('--hover-border-rgba', theme.hoverBorderRGBAHex);

        this.element.style.setProperty('--height', this.fontTheme.heightInPx + 'px');
        this.element.style.setProperty('--half-height', this.fontTheme.lineHeight + 'px');

        if (theme.doTextSpinAnimation) {
            this.element.style.setProperty('--neg-half-height', -this.fontTheme.lineHeight + 'px');
        } else {
            this.element.style.setProperty('--neg-half-height', 0);
        }

        this.element.style.setProperty('--font-size', this.fontTheme.fontSize);
        this.element.style.setProperty('--font-family', this.fontTheme.fontFamily);
        this.element.style.setProperty('--font-weight', this.fontTheme.fontWeight);
        this.element.style.setProperty('--border-radius', theme.borderRadius);

        switch (this.fontTheme.textAlignment) {
            case 'center': {
                this.element.style.setProperty('--text-margin-left', 'auto');
                this.element.style.setProperty('--text-margin-right', 'auto');
                break;
            }
            case 'left': {
                this.element.style.setProperty('--text-margin-left', '0');
                this.element.style.setProperty('--text-margin-right', 'auto');
                break;
            }
            case 'right': {
                this.element.style.setProperty('--text-margin-left', 'auto');
                this.element.style.setProperty('--text-margin-right', '0');
                break;
            }
        }
    }

    // setActive(active: boolean) {
    //     this.active = active;
    // }
}
